import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FileService } from '../services/file';
import { FilePreviewService } from './file-preview';
import { ToastService } from '../services/toast';

/**
 * Reusable document uploader that works as a form control: its value is the S3
 * **guid** the backend returns. Drop it into a Reactive Form via formControlName.
 *
 * Pick → client-validate → upload → emit the guid → show a View link + Remove.
 * On load, a saved guid arrives via writeValue() and renders the View link
 * (a fresh presigned URL is fetched on click). Disabled with the form (e.g. once
 * KYC is approved) via setDisabledState().
 */
@Component({
  selector: 'app-doc-upload',
  standalone: true,
  imports: [ButtonModule],
  template: `
    <div class="doc-upload">
      <input #f type="file" class="hidden-input" accept=".jpg,.jpeg,.png,.pdf,.docx"
             (change)="onPick(f.files); f.value = ''" [disabled]="disabled" />

      <p-button [label]="guid() ? 'Replace' : 'Upload'" icon="pi pi-upload" size="small"
                [outlined]="true" [disabled]="disabled || busy()" (onClick)="f.click()" />

      @if (busy()) {
        <span class="status"><i class="pi pi-spin pi-spinner"></i> Uploading…</span>
      } @else if (guid()) {
        <a class="view" (click)="view()"><i class="pi pi-eye"></i> {{ name() || 'View document' }}</a>
        @if (!disabled) {
          <p-button icon="pi pi-times" severity="danger" [text]="true" size="small" (onClick)="clear()" />
        }
      } @else {
        <span class="hint">No file uploaded</span>
      }
    </div>
  `,
  styles: [
    `
      .doc-upload { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .hidden-input { display: none; }
      .status { font-size: 13px; color: #6b7280; display: inline-flex; align-items: center; gap: 6px; }
      .status i { color: #007cc2; }
      .view {
        font-size: 13px;
        font-weight: 600;
        color: #007cc2;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        text-decoration: none;
        word-break: break-all;
      }
      .view:hover { text-decoration: underline; }
      .hint { font-size: 12.5px; color: #9ca3af; }
    `,
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DocUploadComponent), multi: true },
  ],
})
export class DocUploadComponent implements ControlValueAccessor {
  private files = inject(FileService);
  private preview = inject(FilePreviewService);
  private toast = inject(ToastService);

  /** Groups the S3 object, e.g. 'PartnerKyc'. */
  @Input() module = 'General';

  guid = signal<string>('');
  name = signal<string>('');
  busy = signal(false);
  disabled = false;

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(v: string): void {
    this.guid.set(v ?? '');
    if (!v) this.name.set('');
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(d: boolean): void {
    this.disabled = d;
  }

  onPick(list: FileList | null) {
    const file = list?.[0];
    if (!file) return;
    const err = this.files.validate(file);
    if (err) {
      this.toast.warning(err);
      return;
    }
    this.busy.set(true);
    this.files.upload(file, this.module).subscribe({
      next: (res) => {
        this.busy.set(false);
        this.guid.set(res.guid);
        this.name.set(res.fileName);
        this.onChange(res.guid);
        this.onTouched();
      },
      error: (e) => {
        this.busy.set(false);
        this.toast.error(e?.message || 'Upload failed');
      },
    });
  }

  view() {
    if (!this.guid()) return;
    this.preview.open(this.guid(), this.name());
  }

  clear() {
    this.guid.set('');
    this.name.set('');
    this.onChange('');
    this.onTouched();
  }
}
